/**
 * Driver-availability helpers for the landscape/pipeline surfaces: given the installed
 * driver list (GET /api/v1/drivers), can the platform actually connect to an engine?
 *
 * Engine strings arrive from many vocabularies — the manager's port classifier
 * (postgresql/mysql/sqlserver/oracle), RDS engine ids (aurora-mysql, oracle-ee,
 * sqlserver-ex), and the editor's connection types (mssql, oracledb) — so everything is
 * folded onto the worker driver catalog's engine keys before comparison.
 */

const ENGINE_ALIASES = {
  postgres: 'postgresql',
  'aurora-postgresql': 'postgresql',
  'aurora-mysql': 'mysql',
  mssql: 'sqlserver',
  oracledb: 'oracle',
};

export const normalizeEngineKey = (engine) => {
  if (!engine) return '';
  const e = String(engine).trim().toLowerCase();
  if (ENGINE_ALIASES[e]) return ENGINE_ALIASES[e];
  // RDS flavor suffixes: oracle-ee/oracle-se2 → oracle, sqlserver-ex/-se/-web/-ee → sqlserver.
  if (e.startsWith('oracle')) return 'oracle';
  if (e.startsWith('sqlserver')) return 'sqlserver';
  return e;
};

// The worker bundles the PostgreSQL and MariaDB drivers, and serves `jdbc:mysql:` URLs
// through the bundled MariaDB Connector/J — so these three engines are always connectable
// even when the driver list can't resolve an engine for the bundled rows.
export const BUNDLED_ENGINES = ['postgresql', 'mariadb', 'mysql'];

// Engines that don't go through the JDBC driver registry at all — the worker connects with
// a bundled native driver (MongoDB driver / AWS SDK for DynamoDB), so they're always
// connectable and must never gate on the installed-driver list.
export const NON_JDBC_ENGINES = ['mongodb', 'dynamodb'];

/**
 * Set of normalized engine keys with a usable driver: the always-bundled engines plus
 * every installed driver row with `available === true`. A MariaDB driver also satisfies
 * MySQL (MariaDB Connector/J answers `jdbc:mysql:` URLs); the reverse does not hold.
 */
export const enginesWithDrivers = (drivers = []) => {
  const engines = new Set(BUNDLED_ENGINES);
  (drivers || []).forEach((d) => {
    if (!d || d.available !== true) return;
    const key = normalizeEngineKey(d.engine || d.typeName);
    if (key) engines.add(key);
  });
  if (engines.has('mariadb')) engines.add('mysql');
  return engines;
};

/**
 * True when the platform has a driver for `engine`. An empty/unknown engine returns true —
 * we can't prove a driver is missing, so don't steer the user to the driver page.
 */
export const engineHasDriver = (engine, drivers = []) => {
  const key = normalizeEngineKey(engine);
  if (!key) return true;
  if (NON_JDBC_ENGINES.includes(key)) return true;
  return enginesWithDrivers(drivers).has(key);
};
